import * as React from "react";

import { api } from "@repo/backend";
import type { Id } from "@repo/backend/convex/_generated/dataModel";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import { Calendar } from "@repo/ui/components/calendar";
import { Checkbox } from "@repo/ui/components/checkbox";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "@repo/ui/components/combobox";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import { DataTableColumnHeader } from "@repo/ui/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@repo/ui/components/data-table/data-table-toolbar";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from "@repo/ui/components/field";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@repo/ui/components/hover-card";
import { QRCode } from "@repo/ui/components/qr-code";
import { RelativeTimeCard } from "@repo/ui/components/relative-time-card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@repo/ui/components/sheet";
import {
  Sortable,
  SortableContent,
  SortableItem,
  SortableItemHandle,
} from "@repo/ui/components/sortable";
import { useDataTable } from "@repo/ui/hooks/use-data-table";
import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { type StockFeatures, ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery } from "convex/react";
import {
  Copy,
  GripVertical,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import QR from "qrcode";
import { toast } from "sonner";

import { DataTable } from "@/components/data-table";
import { TimePicker12Demo } from "@/components/time-picker-12h";
import { featureFlags } from "@/config";

export const Route = createFileRoute("/_authed/poses/qr-codes")({
  head: () => ({ meta: [{ title: "Poses QR Codes" }] }),
  component: QRCodeDashboard,
  // TODO: Use loader pattern hehre to support prefetechcing
});

type QrCodeEntry = (typeof api.poses.list)["_returnType"][number];
type Category = (typeof api.poses.listCategories)["_returnType"][number];
type Game = (typeof api.gameTitles.list)["_returnType"][number];
type CategoryOption = {
  id: string;
  title: string;
};

const createDefaultExpiresAt = () => new Date(Date.now() + 1000 * 60 * 60 * 24);

const QR_DRAFT_CREATE_KEY = "qr-draft-create";
const QR_DRAFT_EDIT_KEY_PREFIX = "qr-draft-edit-";
const QR_DRAFT_TTL_MS = 30_000;

type QrCodeDraft = {
  gameId: string;
  categories: string[];
  expiresAt: number; // epoch ms
  closedAt: number; // epoch ms
};

const saveDraft = (
  key: string,
  values: { gameId: string; categories: string[]; expiresAt: Date },
) => {
  const draft: QrCodeDraft = {
    gameId: values.gameId,
    categories: values.categories,
    expiresAt: values.expiresAt.getTime(),
    closedAt: Date.now(),
  };
  localStorage.setItem(key, JSON.stringify(draft));
};

const loadDraft = (key: string): QrCodeDraft | null => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const draft: QrCodeDraft = JSON.parse(raw);
    if (Date.now() - draft.closedAt > QR_DRAFT_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

const clearDraft = (key: string) => {
  localStorage.removeItem(key);
};

const getQrCodeFormValues = (entry?: QrCodeEntry) => ({
  gameId: entry?.gameId ? String(entry.gameId) : "",
  categories: entry?.poses.map((requirement) => String(requirement.categoryId)) ?? [],
  expiresAt: entry ? new Date(entry.expiresAt) : createDefaultExpiresAt(),
});

const printInstruction =
  "Scan this QR code to open your pose challenge. Complete the required poses and follow the on-screen instructions.";

const getQrTargetUrl = (token: string) => {
  const baseUrl = import.meta.env.VITE_QR_FLOW_APP_URL ?? "http://localhost:3001";
  const path = featureFlags.hideSelectedPoses ? `/poses/${token}/s/shared` : `/poses/${token}`;
  return new URL(path, baseUrl).toString();
};

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const buildPrintMarkup = async (entry: QrCodeEntry) => {
  const qrSvg = await QR.toString(getQrTargetUrl(entry.token), {
    type: "svg",
    width: 1400,
    errorCorrectionLevel: "H",
    margin: 0,
    color: {
      dark: "#111111",
      light: "#ffffff",
    },
  });

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(entry.title)} QR</title>
    <style>
      @page {
        size: A4 portrait;
        margin: 10mm;
      }

      * {
        box-sizing: border-box;
      }

      html,
      body {
        width: 210mm;
        height: 297mm;
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: #111111;
        font-family: Arial, sans-serif;
        overflow: hidden;
      }

      @media print {
        html,
        body {
          width: 210mm;
          height: 297mm;
          overflow: hidden;
        }
      }

      .page {
        width: 190mm;
        height: 277mm;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        break-after: avoid-page;
        page-break-after: avoid;
      }

      .sheet {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12mm;
        text-align: center;
      }

      h1 {
        margin: 0;
        max-width: 170mm;
        font-size: 44pt;
        line-height: 1.05;
        font-weight: 700;
      }

      p {
        margin: 0;
        max-width: 160mm;
        font-size: 16pt;
        line-height: 1.35;
      }

      .qr {
        width: 150mm;
        height: 150mm;
        padding: 5mm;
        border: 1px solid #d4d4d4;
        flex: none;
      }

      .qr svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    </style>
  </head>
  <body>
    <main class="page">
      <section class="sheet">
        <h1>${escapeHtml(entry.title)}</h1>
        <div class="qr">${qrSvg}</div>
        <p>${escapeHtml(printInstruction)}</p>
      </section>
    </main>
    <script>
      window.addEventListener("load", () => {
        window.setTimeout(() => window.print(), 120);
      });
      window.addEventListener("afterprint", () => {
        window.close();
      });
    </script>
  </body>
</html>`;
};

function QRCodeDashboard() {
  const confirm = useConfirmationDialog();
  const posesList = useQuery(api.poses.list);
  const categories = useQuery(api.poses.listCategories);
  const games = useQuery(api.gameTitles.list);
  const deleteQrCode = useMutation(api.poses.deleteQrCode).withOptimisticUpdate(
    (localStore, args) => {
      const qrCodes = localStore.getQuery(api.poses.list, {});
      if (!qrCodes) return;

      localStore.setQuery(
        api.poses.list,
        {},
        qrCodes.filter((item) => item._id !== args.qrCodeId),
      );
    },
  );
  const handlePrint = React.useCallback(async (entry: QrCodeEntry) => {
    try {
      const printWindow = window.open("", "_blank", "width=900,height=1200");

      if (!printWindow) {
        toast.error("Allow pop-ups to print this QR code.");
        return;
      }

      const markup = await buildPrintMarkup(entry);

      printWindow.document.open();
      printWindow.document.write(markup);
      printWindow.document.close();
      printWindow.focus();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not open the print view.");
    }
  }, []);

  const columns = React.useMemo<ColumnDef<StockFeatures, QrCodeEntry>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select row ${row.index + 1}`}
          />
        ),
        size: 32,
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "qrCode",
        accessorKey: "token",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="QR Code" label="QR Code" />
        ),
        cell: ({ row }) => <QrCodePreviewCell entry={row.original} />,
      },
      {
        id: "game",
        accessorFn: (entry) => entry.gameName ?? entry.title,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Game" label="Game" />,
        size: 240,
      },
      {
        id: "categories",
        accessorKey: "poses",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Categories" label="Categories" />
        ),
        cell: ({ getValue }) => {
          const requirements = getValue() as QrCodeEntry["poses"];
          return requirements.map((requirement) => (
            <Badge
              key={`${requirement.categoryName ?? "unknown"}-${requirement.order}`}
              className="mr-1"
            >
              {requirement.categoryName ?? "Unknown"}
            </Badge>
          ));
        },
      },
      {
        id: "links",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Links" label="Links" />
        ),
        size: 360,
        meta: { grow: true },
        cell: ({ row }) => <CopyableUrl value={getQrTargetUrl(row.original.token)} label="Pose" />,
      },
      {
        id: "expiresAt",
        accessorKey: "expiresAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Expires At" label="Expires At" />
        ),
        size: 220,
        cell: ({ row }) => <RelativeTimeCard date={new Date(row.original.expiresAt)} />,
      },
      // {
      //   id: "createdAt",
      //   accessorKey: "",
      //   header: ({ column }) => (
      //     <DataTableColumnHeader column={column} title="Created At" label="Created At" />
      //   ),
      //   size: 220,
      //   cell: ({ row }) => <RelativeTimeCard date={new Date(row.original._creationTime)} />,
      // },
      {
        id: "actions",
        header: "Actions",
        size: 160,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2">
              <EditQRCodeSheet entry={row.original} categories={categories} games={games} />
              <Button
                size="icon-lg"
                variant="outline"
                onClick={() => void handlePrint(row.original)}
              >
                <PrinterIcon /> <span className="sr-only">Print</span>
              </Button>
              <Button
                size="icon-lg"
                variant="destructive"
                onClick={async () => {
                  const allowed = await confirm({
                    title: "Delete QR Code",
                    description:
                      "Are you sure you want to delete this QR code? This action cannot be undone.",
                    confirmText: "Delete",
                  });

                  if (!allowed) return;
                  try {
                    await deleteQrCode({
                      qrCodeId: row.original._id,
                    });
                    toast.success("QR code deleted.");
                  } catch (error) {
                    toast.error(
                      error instanceof Error ? error.message : "Could not delete the QR code.",
                    );
                  }
                }}
              >
                <Trash2Icon /> <span className="sr-only">Delete</span>
              </Button>
            </div>
          );
        },
      },
    ],
    [categories, confirm, deleteQrCode, games, handlePrint],
  );

  const data = posesList ?? [];

  const table = useDataTable({
    columns,
    data,
  });

  return (
    <DataTable
      table={table}
      status={"Exhausted"}
      loadMore={() => {}}
      //   actionBar={<TableActionBar table={table} />}
      className="p-4"
    >
      <DataTableToolbar table={table}>
        <CreateQRCodeSheet categories={categories} games={games} />
      </DataTableToolbar>
    </DataTable>
  );
}

function QrCodePreviewCell({ entry }: { entry: QrCodeEntry }) {
  const targetUrl = React.useMemo(() => getQrTargetUrl(entry.token), [entry.token]);

  return (
    <HoverCard>
      <HoverCardTrigger
        render={
          <button
            type="button"
            className="rounded-md transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            aria-label={`Preview destination for ${entry.title}`}
          />
        }
      >
        <QRCode data={targetUrl} />
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-80 p-3">
        <div className="flex flex-col gap-2">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">{entry.title}</p>
            <p className="text-xs text-muted-foreground">QR destination preview</p>
          </div>
          <CopyableUrl value={targetUrl} label="Pose" />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function CopyableUrl({ value, label }: { value: string; label: string }) {
  return (
    <button
      type="button"
      className="flex max-w-72 cursor-pointer items-center gap-2 truncate rounded-md text-left transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      onClick={() =>
        void navigator.clipboard.writeText(value).then(() => toast.success(`${label} URL copied.`))
      }
    >
      <Copy className="size-3.5 shrink-0" />
      <span className="shrink-0 text-foreground">{label}</span>
      <span className="truncate font-mono text-xs text-muted-foreground">{value}</span>
    </button>
  );
}

function QRCodeGameField({
  id,
  value,
  onChange,
  games,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  games: Game[];
}) {
  const gameIds = React.useMemo(() => games.map((game) => String(game["_id"])), [games]);
  const gameNameById = React.useMemo(
    () => new Map(games.map((game) => [String(game["_id"]), game.name])),
    [games],
  );

  return (
    <Combobox
      items={gameIds}
      itemToStringValue={(gameId) => gameNameById.get(gameId) ?? gameId}
      value={value || null}
      onValueChange={(nextValue) => onChange(nextValue ?? "")}
    >
      <ComboboxInput id={id} className="w-full" placeholder="Search game titles" showClear />
      <ComboboxContent>
        <ComboboxEmpty>No games found.</ComboboxEmpty>
        <ComboboxList>
          {(gameId) => (
            <ComboboxItem key={gameId} value={gameId}>
              {gameNameById.get(gameId) ?? gameId}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

function QRCodeCategoriesField({
  id,
  value,
  onChange,
  categoryOptions,
  categoryTitleById,
}: {
  id: string;
  value: string[];
  onChange: (value: string[]) => void;
  categoryOptions: CategoryOption[];
  categoryTitleById: Map<string, string>;
}) {
  const categoryIds = React.useMemo(
    () => categoryOptions.map((category) => category.id),
    [categoryOptions],
  );
  const selectedCategories = React.useMemo(
    () =>
      value.map((categoryId) => ({
        id: categoryId,
        title: categoryTitleById.get(categoryId) ?? categoryId,
      })),
    [categoryTitleById, value],
  );

  return (
    <div className="space-y-3">
      <Combobox items={categoryIds} value={value} onValueChange={onChange} multiple>
        <ComboboxChips id={id}>
          <ComboboxValue>
            {value.map((item) => (
              <ComboboxChip key={item}>{categoryTitleById.get(item) ?? item}</ComboboxChip>
            ))}
          </ComboboxValue>
          <ComboboxChipsInput placeholder="Select categories" />
        </ComboboxChips>
        <ComboboxContent>
          <ComboboxEmpty>No items found.</ComboboxEmpty>
          <ComboboxList>
            {(item) => (
              <ComboboxItem key={item} value={item}>
                {categoryTitleById.get(item) ?? item}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>

      {selectedCategories.length ? (
        <div className="space-y-2">
          <FieldDescription>
            Drag to reorder. The first eight categories map to the game's CUT slots in Renamer.
          </FieldDescription>
          <Sortable
            value={selectedCategories}
            getItemValue={(item) => item.id}
            onValueChange={(items) => onChange(items.map((item) => item.id))}
          >
            <SortableContent className="space-y-2">
              {selectedCategories.map((category, index) => (
                <SortableItem key={category.id} value={category.id}>
                  <div className="flex items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-background text-xs font-medium text-muted-foreground">
                      {index + 1}
                    </div>
                    <p className="min-w-0 flex-1 truncate text-sm font-medium">{category.title}</p>
                    <SortableItemHandle
                      className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Reorder ${category.title}`}
                    >
                      <GripVertical className="size-4" />
                    </SortableItemHandle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${category.title}`}
                      onClick={() => onChange(value.filter((item) => item !== category.id))}
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </div>
                </SortableItem>
              ))}
            </SortableContent>
          </Sortable>
        </div>
      ) : null}
    </div>
  );
}

function CreateQRCodeSheet({
  categories,
  games,
}: {
  categories: Category[] | undefined;
  games: Game[] | undefined;
}) {
  const createQrCode = useMutation(api.poses.create);
  const [open, setOpen] = React.useState(false);
  const categoryOptions = React.useMemo(
    () =>
      categories?.map((category) => ({ id: String(category._id), title: category.title })) ?? [],
    [categories],
  );
  const categoryTitleById = React.useMemo(
    () => new Map(categoryOptions.map((category) => [category.id, category.title])),
    [categoryOptions],
  );

  const form = useForm({
    defaultValues: getQrCodeFormValues(),
    onSubmit: async ({ value }) => {
      if (!value.gameId) {
        toast.error("Choose a game.");
        return;
      }

      if (!value.categories.length) {
        toast.error("Choose at least one category.");
        return;
      }

      try {
        await createQrCode({
          gameId: value.gameId as Id<"gameTitles">,
          categories: value.categories as Id<"poseCategories">[],
          expiresAt: value.expiresAt.getTime(),
        });

        clearDraft(QR_DRAFT_CREATE_KEY);
        form.reset(getQrCodeFormValues());
        setOpen(false);
        toast.success("QR code created.");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not create the QR code.");
      }
    },
  });

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        const { gameId, categories: selectedCategoryIds, expiresAt } = form.state.values;
        const hasChanges =
          gameId !== "" ||
          selectedCategoryIds.length > 0 ||
          expiresAt.getTime() !== createDefaultExpiresAt().getTime();
        if (hasChanges) {
          saveDraft(QR_DRAFT_CREATE_KEY, {
            gameId,
            categories: selectedCategoryIds,
            expiresAt,
          });
        }
      } else {
        const draft = loadDraft(QR_DRAFT_CREATE_KEY);
        if (draft) {
          form.reset({
            gameId: draft.gameId ?? "",
            categories: draft.categories,
            expiresAt: new Date(draft.expiresAt),
          });
          clearDraft(QR_DRAFT_CREATE_KEY);
        }
      }
      setOpen(nextOpen);
    },
    [form],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger
        render={(props) => (
          <Button {...props} className="gap-2">
            <PlusIcon />
            New QR Code
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent>
        <form
          className="flex h-full flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Create QR Code</SheetTitle>
            <SheetDescription>
              Choose the game and ordered pose categories. Renamer uses these category names for
              that game's CUT slots.
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col justify-between overflow-y-auto px-4">
            <div className="grid auto-rows-min gap-6">
              <FieldSet>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="game">Game</FieldLabel>
                    <form.Field name="gameId">
                      {(field) => (
                        <QRCodeGameField
                          id="game"
                          value={field.state.value}
                          onChange={field.handleChange}
                          games={games ?? []}
                        />
                      )}
                    </form.Field>
                    <FieldDescription>
                      One Pose QR code can be assigned to each game.
                    </FieldDescription>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="categories">Categories</FieldLabel>
                    <form.Field name="categories">
                      {(field) => (
                        <QRCodeCategoriesField
                          id="categories"
                          value={field.state.value}
                          onChange={field.handleChange}
                          categoryOptions={categoryOptions}
                          categoryTitleById={categoryTitleById}
                        />
                      )}
                    </form.Field>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="expiresAt">Expires At</FieldLabel>
                    <form.Field name="expiresAt">
                      {(field) => (
                        <>
                          {/* TODO: Simplify this calendar and time picker inputs into a single dropdown */}
                          <Calendar
                            mode="single"
                            selected={field.state.value}
                            onSelect={(date) => date && field.setValue(date)}
                            className="rounded-lg border"
                          />
                          <TimePicker12Demo
                            date={field.state.value}
                            setDate={(date) => date && field.setValue(date)}
                          />
                        </>
                      )}
                    </form.Field>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>
          </div>
          <div className="flex w-full gap-1 p-4">
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Button
                  className="flex-1"
                  type="submit"
                  disabled={
                    !canSubmit || isSubmitting || !categoryOptions.length || !(games?.length ?? 0)
                  }
                >
                  Create QR Code
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function EditQRCodeSheet({
  entry,
  categories,
  games,
}: {
  entry: QrCodeEntry;
  categories: Category[] | undefined;
  games: Game[] | undefined;
}) {
  const updateQrCode = useMutation(api.poses.updateQrCode).withOptimisticUpdate(
    (localStore, args) => {
      const qrCodes = localStore.getQuery(api.poses.list, {});
      if (!qrCodes) return;

      const categoryTitleById = new Map(
        (categories ?? []).map((category) => [String(category._id), category.title]),
      );
      const gameName = games?.find((game) => game["_id"] === args.gameId)?.name ?? "Unknown game";

      localStore.setQuery(
        api.poses.list,
        {},
        qrCodes.map((item) =>
          item._id === args.qrCodeId
            ? {
                ...item,
                title: gameName,
                gameId: args.gameId,
                gameName,
                expiresAt: args.expiresAt,
                poses: args.categories.map((categoryId, order) => ({
                  categoryId,
                  requiredCount: 1,
                  order,
                  categoryName: categoryTitleById.get(String(categoryId)) ?? null,
                })),
              }
            : item,
        ),
      );
    },
  );
  const [open, setOpen] = React.useState(false);
  const draftKey = React.useMemo(
    () => `${QR_DRAFT_EDIT_KEY_PREFIX}${String(entry._id)}`,
    [entry._id],
  );
  const categoryOptions = React.useMemo(
    () =>
      categories?.map((category) => ({ id: String(category._id), title: category.title })) ?? [],
    [categories],
  );
  const categoryTitleById = React.useMemo(
    () => new Map(categoryOptions.map((category) => [category.id, category.title])),
    [categoryOptions],
  );
  const form = useForm({
    defaultValues: getQrCodeFormValues(entry),
    onSubmit: async ({ value }) => {
      if (!value.gameId) {
        toast.error("Choose a game.");
        return;
      }

      if (!value.categories.length) {
        toast.error("Choose at least one category.");
        return;
      }

      try {
        await updateQrCode({
          qrCodeId: entry._id,
          gameId: value.gameId as Id<"gameTitles">,
          categories: value.categories as Id<"poseCategories">[],
          expiresAt: value.expiresAt.getTime(),
        });

        clearDraft(draftKey);
        setOpen(false);
        toast.success("QR code updated.");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not update the QR code.");
      }
    },
  });

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const draft = loadDraft(draftKey);
        if (draft) {
          form.reset({
            gameId: draft.gameId ?? "",
            categories: draft.categories,
            expiresAt: new Date(draft.expiresAt),
          });
          clearDraft(draftKey);
        } else {
          form.reset(getQrCodeFormValues(entry));
        }
      } else {
        const { gameId, categories: selectedCategoryIds, expiresAt } = form.state.values;
        const defaults = getQrCodeFormValues(entry);
        const hasChanges =
          gameId !== defaults.gameId ||
          JSON.stringify(selectedCategoryIds) !== JSON.stringify(defaults.categories) ||
          expiresAt.getTime() !== defaults.expiresAt.getTime();
        if (hasChanges) {
          saveDraft(draftKey, { gameId, categories: selectedCategoryIds, expiresAt });
        }
      }
      setOpen(nextOpen);
    },
    [draftKey, entry, form],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger
        render={(props) => (
          <Button {...props} size="icon-lg" variant="secondary">
            <PencilIcon /> <span className="sr-only">Edit</span>
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent>
        <form
          className="flex h-full flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Edit QR Code</SheetTitle>
            <SheetDescription>
              Update the assigned game, category order, and expiry without changing its link.
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col justify-between overflow-y-auto px-4">
            <div className="grid auto-rows-min gap-6">
              <FieldSet>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor={`game-${String(entry._id)}`}>Game</FieldLabel>
                    <form.Field name="gameId">
                      {(field) => (
                        <QRCodeGameField
                          id={`game-${String(entry._id)}`}
                          value={field.state.value}
                          onChange={field.handleChange}
                          games={games ?? []}
                        />
                      )}
                    </form.Field>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`categories-${String(entry._id)}`}>Categories</FieldLabel>
                    <form.Field name="categories">
                      {(field) => (
                        <QRCodeCategoriesField
                          id={`categories-${String(entry._id)}`}
                          value={field.state.value}
                          onChange={field.handleChange}
                          categoryOptions={categoryOptions}
                          categoryTitleById={categoryTitleById}
                        />
                      )}
                    </form.Field>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`expiresAt-${String(entry._id)}`}>Expires At</FieldLabel>
                    <form.Field name="expiresAt">
                      {(field) => (
                        <>
                          <Calendar
                            mode="single"
                            selected={field.state.value}
                            onSelect={(date) => date && field.setValue(date)}
                            className="rounded-lg border"
                          />
                          <TimePicker12Demo
                            date={field.state.value}
                            setDate={(date) => date && field.setValue(date)}
                          />
                        </>
                      )}
                    </form.Field>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>
          </div>
          <div className="flex w-full gap-1 p-4">
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Button
                  className="flex-1"
                  type="submit"
                  disabled={
                    !canSubmit || isSubmitting || !categoryOptions.length || !(games?.length ?? 0)
                  }
                >
                  Save changes
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
